import axios from 'axios';

class SessionManagementService {
  /**
   * Get all active editing sessions for the current user
   * 
   * @param {string} email - User email
   * @param {string} authToken - Authentication token
   * @returns {Promise} - Promise resolving to API response with active sessions
   */
  async getActiveSessions(email, authToken) {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/v3/sessions/active`,
        {
          headers: {
            email: email,
            auth: authToken
          }
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching active sessions:', error);
      throw error;
    }
  }

  /**
   * End a specific editing session
   * 
   * @param {string} videoId - Video ID
   * @param {string} languageName - Language name
   * @param {string} email - User email
   * @param {string} authToken - Authentication token
   * @returns {Promise} - Promise resolving to API response
   */
  async endSession(videoId, languageName, email, authToken) {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v3/videos/${videoId}/languages/${languageName}/edit-session/end`,
        {},
        {
          headers: {
            email: email,
            auth: authToken
          }
        }
      );
      return response;
    } catch (error) {
      console.error('Error ending session:', error);
      throw error;
    }
  }

  /**
   * End all active editing sessions for the current user
   * 
   * @param {string} email - User email
   * @param {string} authToken - Authentication token
   * @returns {Promise} - Promise resolving to API response
   */
  async endAllSessions(email, authToken) {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v3/sessions/end-all`,
        {},
        {
          headers: {
            email: email,
            auth: authToken
          }
        }
      );
      return response;
    } catch (error) {
      console.error('Error ending all sessions:', error);
      throw error;
    }
  }

  /**
   * Get all active editing sessions across all users (admin only)
   * 
   * @param {string} email - Admin email
   * @param {string} authToken - Authentication token
   * @returns {Promise} - Promise resolving to API response with active sessions
   */
  async getAllUsersSessions(email, authToken) {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/v3/sessions/all-users`,
        {
          headers: {
            email: email,
            auth: authToken
          }
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching all users sessions:', error);
      throw error;
    }
  }

  /**
   * End a session for a specific user (admin only)
   * 
   * @param {string} videoId - Video ID
   * @param {string} languageName - Language name
   * @param {string} userEmail - Email of the user whose session to end
   * @param {string} adminEmail - Admin email
   * @param {string} authToken - Authentication token
   * @returns {Promise} - Promise resolving to API response
   */
  async endUserSession(videoId, languageName, userEmail, adminEmail, authToken) {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v3/videos/${videoId}/languages/${languageName}/edit-session/end`,
        {},
        {
          headers: {
            email: adminEmail,
            auth: authToken
          },
          params: {
            userEmail: userEmail
          }
        }
      );
      return response;
    } catch (error) {
      console.error('Error ending user session:', error);
      throw error;
    }
  }

  /**
   * End a specific section editing session
   * 
   * @param {string} videoId - Video ID
   * @param {string} languageName - Language name
   * @param {string} sectionId - Section ID
   * @param {string} email - User email
   * @param {string} authToken - Authentication token
   * @returns {Promise} - Promise resolving to API response
   */
  async endSectionSession(videoId, languageName, sectionId, email, authToken) {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v3/videos/${videoId}/languages/${languageName}/sections/${sectionId}/edit/end`,
        {},
        {
          headers: {
            email: email,
            auth: authToken
          }
        }
      );
      return response;
    } catch (error) {
      console.error('Error ending section session:', error);
      throw error;
    }
  }

  /**
   * End a section session for a specific user (admin only)
   * 
   * @param {string} videoId - Video ID
   * @param {string} languageName - Language name
   * @param {string} sectionId - Section ID
   * @param {string} userEmail - Email of the user whose session to end
   * @param {string} adminEmail - Admin email
   * @param {string} authToken - Authentication token
   * @returns {Promise} - Promise resolving to API response
   */
  async endUserSectionSession(videoId, languageName, sectionId, userEmail, adminEmail, authToken) {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v3/videos/${videoId}/languages/${languageName}/sections/${sectionId}/edit/admin-end`,
        { userEmail },
        {
          headers: {
            email: adminEmail,
            auth: authToken
          }
        }
      );
      return response;
    } catch (error) {
      console.error('Error ending user section session:', error);
      throw error;
    }
  }

  /**
   * End a text file editing session
   */
  async endTextFileSession(fileId, languageName, email, authToken) {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v2/textfiles/${fileId}/languages/${languageName}/edit-session/end`,
        {},
        { headers: { email, auth: authToken } },
      );
      return response;
    } catch (error) {
      console.error('Error ending text file session:', error);
      throw error;
    }
  }

  /**
   * End a text file session for a specific user (admin only)
   */
  async endUserTextFileSession(fileId, languageName, userEmail, adminEmail, authToken) {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v2/textfiles/${fileId}/languages/${languageName}/edit-session/end`,
        {},
        {
          headers: { email: adminEmail, auth: authToken },
          params: { userEmail },
        },
      );
      return response;
    } catch (error) {
      console.error('Error ending user text file session:', error);
      throw error;
    }
  }

  /**
   * Get users who have been active (heartbeat) within the last N minutes.
   * Admin only.
   *
   * @param {string} email - Admin email
   * @param {string} authToken - Authentication token
   * @param {number} minutes - Lookback window in minutes (default 30)
   * @returns {Promise} - Promise resolving to list of active user objects
   */
  async getLoggedInUsers(email, authToken, minutes = 30) {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/v2/users/active`,
        {
          headers: { email, auth: authToken },
          params: { minutes },
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching logged-in users:', error);
      throw error;
    }
  }
}

export default new SessionManagementService();